import { BottomDockObserver, isIntersectionObserverSupported } from '@Shared/Common';
/**
 * ActionToolBarObserver creates three separate observers to handle how the toolbar is being displayed.
 * One observer to handle the mobile toolbar. One to handle when the toolbar should stick to the top
 * of the screen on desktop. The other to listen to when the toolbar should dock at the bottom of the page container.
 */

var actionToolbarObserver = function actionToolbarObserver(config) {
  var external = {};
  config = config || {};
  var internal = {
    pageContainerTargetSelector: ".pageContainer",
    actionToolbarSelector: '.floatingBox .actionToolbar',
    adhesiveHeaderAdClass: "adhesiveHeaderAd",
    mobileRdpTargetTopClass: '.recipeContentTop',
    mobileRdpTargetBottomClass: '.recipeContentBottom',
    mobilePdpTargetTopClass: '.productContentTop',
    mobilePdpTargetBottomClass: '.productContentBottom',
    desktopDockObserver: null,
    mobileActionToolbarSelector: '.mobileActionToolbar .floatingBox .actionToolbar'
  };
  internal.config = {
    actionToolBarClass: "stickyBottom",
    testMode: !!config.testMode,
    anchoredTopClass: "anchorTop",
    anchoredBottomClass: "anchorBottom"
  };

  if (config.testMode === true) {
    external.internal = internal;
  }
  /**
   * @function initialize
   * Initializes variables such as the target elements to observer.
   * Also sets the observer options and class the functions to create the observers.
   */


  external.initialize = function () {
    //Do not use observers if it's the RDP redesigned page (the new RDP layout uses the position sticky CSS property instead).
    var redesignedRdp = document.querySelector(".recipeDetail.redesign");
    if (redesignedRdp) return; //check if action toolbar exists on page

    var actionToolbar = document.querySelector(internal.actionToolbarSelector);
    var mobileActionToolbar = document.querySelector(internal.mobileActionToolbarSelector);
    if (!actionToolbar && !mobileActionToolbar) return; //do nothing if we don't have action toolbar
    //create observers 

    if (mobileActionToolbar && window.getComputedStyle(mobileActionToolbar).display === "block") {
      internal.createMobileDockIntersectionObserver();
      internal.createMobileBottomIntersectionObserver();
    } else {
      internal.addMobileIntersectionCallbacks();
      internal.createDesktopDockIntersectionObserver();
      internal.createDesktopBottomIntersectionObserver(); // add listeners so we know when to reset the desktop observer      

      internal.createAdhesiveAdSpacingObserver();
      window.addEventListener('resize', internal.resetDesktopDockObserver);
    }
  };

  internal.addMobileIntersectionCallbacks = function () {
    BottomDockObserver.addIntersectionHandler(function () {
      internal.toggleToolBarClass();
    });
  };

  internal.calculateRootBottomMargin = function () {
    //calculate offsets for action tool bar docking on desktop
    var offsetAddition = 0;
    var toolbar = document.querySelector(".recipeDetailActionToolbar") || document.querySelector(".productDetailActionToolbar");

    if (toolbar) {
      var toolbarOffset = toolbar.getBoundingClientRect().y;
      var pageContainer = document.querySelector(".pageContainer");
      var pageContainerOffset = pageContainer.getBoundingClientRect().y;
      offsetAddition = toolbarOffset - pageContainerOffset;
    } else {
      var channelLinks = document.querySelector(".channelLinks");
      offsetAddition = channelLinks ? channelLinks.offsetHeight : 0;
    }

    var adhesiveAdSpacing = document.querySelector(".adhesiveAdSpacing");
    var adhesiveAdHeight = adhesiveAdSpacing ? adhesiveAdSpacing.offsetHeight + 110 : 110; //multiply the viewport height by the pixel ratio to account for upscaling

    return window.innerHeight * window.devicePixelRatio + offsetAddition - adhesiveAdHeight;
  };
  /**
  * @function createMobileDockIntersectionObserver
  * Creates the observer that listens for when the viewport is scrolled passed the distance to the top
  * of the recipe content/product content class, which is also the top of the action tool bar when it is in it's default position.
  */


  internal.createMobileDockIntersectionObserver = function () {
    if (!isIntersectionObserverSupported()) {
      internal.logMessage("IntersectionObserver is not available in this browser");
      return;
    }

    var target = internal.getMobileObserverTarget();

    if (target) {
      target.setAttribute('data-observer', 'true');
      var options = {
        root: null,
        //viewport is the root                
        threshold: [0, 0.05]
      };
      internal.desktopDockObserver = new window.IntersectionObserver(internal.handleRdpPdpIntersectionEvent, options);
      internal.desktopDockObserver.observe(target);
    }
  };
  /**
  * @function createMobileBottomIntersectionObserver
  * Creates the bottom observer that observes whether the viewport has scrolled past recipe content/ product content class
  */


  internal.createMobileBottomIntersectionObserver = function () {
    if (!isIntersectionObserverSupported()) {
      internal.logMessage("IntersectionObserver is not available in this browser");
      return;
    }

    var target = internal.getMobileObserverTarget("bottom");

    if (target) {
      var toolbarHeight = document.querySelector(internal.mobileActionToolbarSelector).offsetHeight;
      target.setAttribute('data-observer', 'true');
      var options = {
        root: null,
        //viewport is the root
        rootMargin: "-".concat(toolbarHeight, "px 0px 0px 0px"),
        threshold: [0, 0.05]
      };
      var observer = new window.IntersectionObserver(internal.handleRdpPdpBottomIntersectionEvent, options);
      observer.observe(target);
    }
  };

  internal.getMobileObserverTarget = function (location) {
    var rdpTarget = location == "bottom" ? document.querySelector(internal.mobileRdpTargetBottomClass) : document.querySelector(internal.mobileRdpTargetTopClass);
    if (rdpTarget && rdpTarget.getAttribute('data-observer') == null) return rdpTarget;
    var pdpTarget = location == "bottom" ? document.querySelector(internal.mobilePdpTargetBottomClass) : document.querySelector(internal.mobilePdpTargetTopClass);
    if (pdpTarget && pdpTarget.getAttribute('data-observer') == null) return pdpTarget;
    return null;
  };

  internal.handleRdpPdpIntersectionEvent = function (intersectionEntries) {
    intersectionEntries.forEach(function (entry) {
      if (entry.boundingClientRect.top < 0) internal.addAnchoredTopClass(internal.mobileActionToolbarSelector);else if (entry.boundingClientRect.top > 0) internal.removeAnchoredTopClass(internal.mobileActionToolbarSelector);
    });
  };

  internal.handleRdpPdpBottomIntersectionEvent = function (intersectionEntries) {
    intersectionEntries.forEach(function (entry) {
      if (entry.isIntersecting) internal.addAnchoredTopClass(internal.mobileActionToolbarSelector);else if (entry.boundingClientRect.top < entry.rootBounds.top) internal.removeAnchoredTopClass(internal.mobileActionToolbarSelector);
    });
  };
  /**
   * @function createDesktopDockIntersectionObserver
   * Creates the observer that listens for when the viewport is scrolled passed the distance to the top
   * of the page container, which is also the top of the action tool bar when it is in it's default position.
   */


  internal.createDesktopDockIntersectionObserver = function () {
    if (!isIntersectionObserverSupported()) {
      internal.logMessage("IntersectionObserver is not available in this browser");
      return;
    }

    var target = document.querySelector(internal.pageContainerTargetSelector); //Make sure same observer is not added to the target element again

    if (target.getAttribute('data-dock-observer')) return; //set the rootMargin offsets make it so that the user will have to scroll the full length of the viewport and toolbar
    //set threshold at 0 and 0.05 to make sure there are no missed intersections

    var margin = internal.calculateRootBottomMargin();
    var options = {
      root: null,
      rootMargin: "".concat(margin, "px 0px -").concat(margin, "px 0px"),
      threshold: [0, 0.05]
    };
    internal.desktopDockObserver = new window.IntersectionObserver(internal.handleDesktopDockIntersectionEvent, options);
    internal.desktopDockObserver.observe(target);
    target.setAttribute("data-dock-observer", true);
  };
  /**
   * @function handleDesktopDockIntersectionEvent
   * Function that handles the Desktop Dock observer. Once the top of the viewport intersects with the top of the page
   * container it will fire an event to either add or remove the anchored top class.
   */


  internal.handleDesktopDockIntersectionEvent = function (intersectionEntries) {
    intersectionEntries.forEach(function (entry) {
      if (entry.intersectionRatio > 0) {
        internal.addAnchoredTopClass(internal.actionToolbarSelector);
      } else {
        internal.removeAnchoredTopClass(internal.actionToolbarSelector);
      }
    });
  };
  /**
   * @function createDesktopBottomIntersectionObserver
   * Creates the bottom observer that observes whether the viewport and toolbar offset is scrolled past the bottom of the page container
   */


  internal.createDesktopBottomIntersectionObserver = function () {
    if (!isIntersectionObserverSupported()) {
      internal.logMessage("IntersectionObserver is not available in this browser");
      return;
    }

    var target = document.querySelector(internal.pageContainerTargetSelector); //Make sure same observer is not added to the target element again

    if (target.getAttribute('data-bottom-observer')) return;
    var toolbarHeight = document.querySelector(internal.actionToolbarSelector).offsetHeight;
    var options = {
      root: null,
      rootMargin: "-".concat(toolbarHeight, "px 0px 0px 0px"),
      threshold: [0, 0.05]
    };
    var observer = new window.IntersectionObserver(internal.handleDesktopBottomIntersectionEvent, options);
    observer.observe(target);
    target.setAttribute("data-bottom-observer", true);
  };
  /**
   * @function handleDesktopBottomIntersectionEvent
   * Function that handles the bottom intersections. If it is no longer intersecting, add the anchor bottom class so that
   * the toolbar sticks to the bottom of the page container. Once the user scrolls back up and it intersects again,
   * re-add the anchored top class but make sure that we are not at the top of the page.
   */


  internal.handleDesktopBottomIntersectionEvent = function (intersectionEntries) {
    intersectionEntries.forEach(function (entry) {
      if (entry.intersectionRatio < 0.05) {
        internal.addAnchorBottomClass();
      } else if (entry.intersectionRatio > 0.05 && entry.boundingClientRect.top < 0) {
        internal.addAnchoredTopClass(internal.actionToolbarSelector);
      }
    });
  };
  /**
  * @function toggleToolbarClass
  * Displays or hides the actionToolbar.
  */


  internal.toggleToolBarClass = function () {
    var toolbar = document.querySelector(internal.actionToolbarSelector);
    toolbar.classList.toggle(internal.config.actionToolBarClass);
  };
  /**
   * @function addAnchoredTopClass
   * Removes the anchored bottom class if it exists and adds the anchored top class.
   */


  internal.addAnchoredTopClass = function (actionToolbarCssSelector) {
    var toolbar = document.querySelector(actionToolbarCssSelector);

    if (toolbar.classList.contains(internal.config.anchoredBottomClass)) {
      toolbar.classList.remove(internal.config.anchoredBottomClass);
    }

    toolbar.classList.add(internal.config.anchoredTopClass);
  };
  /**
   * @function removeAnchoredTopClass
   * Removes the anchored top class if it exists on the toolbar.
   */


  internal.removeAnchoredTopClass = function (actionToolbarCssSelector) {
    var toolbar = document.querySelector(actionToolbarCssSelector);

    if (toolbar.classList.contains(internal.config.anchoredTopClass)) {
      toolbar.classList.remove(internal.config.anchoredTopClass);
    }
  };
  /**
   * @function addAnchorBottomClass
   * Adds the anchor bottom class to the toolbar.
   */


  internal.addAnchorBottomClass = function () {
    var toolbar = document.querySelector(internal.actionToolbarSelector);

    if (toolbar.classList.contains(internal.config.anchoredTopClass)) {
      internal.removeAnchoredTopClass(internal.actionToolbarSelector);
    }

    toolbar.classList.add(internal.config.anchoredBottomClass);
  };
  /**
   * @function createAdhesiveAdSpacingObserver
   * Watches for changes in the adhesive header ad to know when it has been closed.
   */


  internal.createAdhesiveAdSpacingObserver = function () {
    var spacing = document.querySelector('.adhesiveAdSpacing');

    if (spacing) {
      var config = {
        attributes: true,
        attributeFilter: ['class']
      };
      var observer = new MutationObserver(this.handleAdhesiveAdSpacingChange);
      observer.observe(spacing, config);
    }
  };
  /**
   * @function handleAdhesiveAdSpacingChange
   * When the adhesive ad spacing removes its visible class, we know the header ad was dismissed.
   * As a result, we need to reset the desktop dock observer to react to the sizing change.
   */


  internal.handleAdhesiveAdSpacingChange = function (mutations) {
    for (var m in mutations) {
      var mutation = mutations[m];

      if (mutation.attributeName === 'class') {
        // adhesive ad spacing has changed so reset the desktop dock 
        internal.resetDesktopDockObserver();
      }
    }
  };
  /**
   *  @function resetDesktopDockObserver
   * Reset the desktop dock observer, triggered when there is a change in the viewport sizing.
   */


  internal.resetDesktopDockObserver = function () {
    //remove desktopdockobserver
    if (internal.desktopDockObserver != null) {
      internal.desktopDockObserver.disconnect();
      var target = document.querySelector(internal.pageContainerTargetSelector);
      target.removeAttribute("data-dock-observer");
      target.removeAttribute("data-bottom-observer");
    } //re add desktopdockobserver


    internal.createDesktopDockIntersectionObserver();
  };

  internal.logMessage = function (message, forceLog) {
    if (external.debugMode || forceLog) {
      console.log("[ActionToolBarObserver] " + message);
    }
  };

  external.initialize();
  return external;
};

export default actionToolbarObserver;